import React, { useState } from 'react';
import { toast } from 'sonner';
import { Lightbulb, PackageSearch, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useSuggestEditMutation } from '@/client/api/listing-management/entity.mutation';

interface Props {
  open: boolean;
  eid: number;
  name: string;
  /** Owner targets — a suggestion is delivered to the owning user or org. */
  ownerUid?: number | null;
  ownerOid?: number | null;
  ownerName?: string | null;
  onClose: () => void;
}

const TEMPLATE = (name: string) =>
  `Hi, this is the FeelyFeely team. We reviewed your listing "${name}" and have a suggestion:\n\n` +
  `• \n\n` +
  `Please update your listing when you get a chance. Thanks!`;

/**
 * Admins don't edit an owner's listing content directly — instead they send the
 * owner a suggestion (a platform message referencing the listing) so the owner
 * makes the change themselves. Delivered via `POST /admin/v2/message-broadcast/send`.
 */
const SuggestEditDialog: React.FC<Props> = ({
  open,
  eid,
  name,
  ownerUid,
  ownerOid,
  ownerName,
  onClose,
}) => {
  const [message, setMessage] = useState(() => TEMPLATE(name));
  const sendMutation = useSuggestEditMutation();

  const hasTarget = Boolean(ownerUid || ownerOid);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Write a suggestion before sending.');
      return;
    }
    if (!hasTarget) {
      toast.error('This listing has no owner to send a suggestion to.');
      return;
    }
    try {
      await sendMutation.mutateAsync({
        eid,
        suggested_change: message.trim(),
      });
      toast.success('Suggestion sent to the owner');
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to send the suggestion.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Suggest an edit
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <p className="text-sm text-gray-500">
            Admins don&apos;t change an owner&apos;s listing directly. Send{' '}
            <span className="font-medium text-gray-700">
              {ownerName || (ownerUid ? `user #${ownerUid}` : ownerOid ? `org #${ownerOid}` : 'the owner')}
            </span>{' '}
            a suggestion for <span className="font-medium text-gray-700">&ldquo;{name}&rdquo;</span> and
            they&apos;ll make the change themselves.
          </p>

          <div className="space-y-1">
            <label htmlFor="suggest-msg" className="text-sm font-medium text-gray-700">
              Suggestion
            </label>
            <textarea
              id="suggest-msg"
              rows={8}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe what the owner should change…"
            />
          </div>

          {!hasTarget && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
              This listing has no linked owner, so a suggestion can&apos;t be delivered.
            </p>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={sendMutation.isPending || !hasTarget}>
              {sendMutation.isPending ? (
                'Sending…'
              ) : (
                <>
                  <Send className="mr-1.5 h-4 w-4" />
                  Send suggestion
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SuggestEditDialog;
